import React, { useMemo, useCallback, useState } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  NodeProps,
  Handle,
  Position,
  useNodesState,
  useEdgesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Database,
  Cloud,
  Server,
  Smartphone,
  Shield,
  Network,
  Lock,
  Zap
} from 'lucide-react';
import { ArchitectureDiagram } from '../../services/processOptimizationAPI';

interface VisualArchitectureDiagramProps {
  architecture: ArchitectureDiagram;
  onSystemClick?: (systemId: string) => void;
}

interface SystemNodeData {
  label: string;
  type: string;
  category: string;
  description: string;
  complexity?: string;
  hasAuth?: boolean;
  authMethod?: string;
  rateLimit?: string;
  errorHandling?: string;
  constraints?: string[];
  onClick?: () => void;
}

// Custom node component for systems
const SystemNode = ({ data }: { data: SystemNodeData }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const getIcon = (type: string) => {
    switch (type) {
      case 'database':
        return <Database className="w-6 h-6" />;
      case 'api':
        return <Cloud className="w-6 h-6" />;
      case 'ui':
        return <Smartphone className="w-6 h-6" />;
      case 'middleware':
        return <Server className="w-6 h-6" />;
      case 'source':
        return <Zap className="w-6 h-6" />;
      case 'target':
        return <Database className="w-6 h-6" />;
      default:
        return <Network className="w-6 h-6" />;
    }
  };

  const getColorsByComplexity = (complexity?: string) => {
    switch (complexity) {
      case 'simple':
        return {
          bg: 'bg-green-50',
          border: 'border-green-400',
          text: 'text-green-800',
          iconBg: 'bg-green-100',
          badge: 'bg-green-600'
        };
      case 'moderate':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-400',
          text: 'text-yellow-800',
          iconBg: 'bg-yellow-100',
          badge: 'bg-yellow-600'
        };
      case 'complex':
        return {
          bg: 'bg-red-50',
          border: 'border-red-400',
          text: 'text-red-800',
          iconBg: 'bg-red-100',
          badge: 'bg-red-600'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-400',
          text: 'text-blue-800',
          iconBg: 'bg-blue-100',
          badge: 'bg-blue-600'
        };
    }
  };

  const colors = getColorsByComplexity(data.complexity);
  const hasTechnicalDetails = data.authMethod || data.rateLimit || data.errorHandling || (data.constraints && data.constraints.length > 0);

  return (
    <div
      className={`${colors.bg} ${colors.border} border-2 rounded-lg p-2 shadow-lg hover:shadow-xl transition-all min-w-[160px] max-w-[200px] relative`}
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
    >
      {/* Input/Output Handles */}
      <Handle type="target" position={Position.Left} className="w-3 h-3" />
      <Handle type="source" position={Position.Right} className="w-3 h-3" />
      <Handle type="target" position={Position.Top} className="w-3 h-3" />
      <Handle type="source" position={Position.Bottom} className="w-3 h-3" />

      {/* Technical Details Indicator Badge - Visible on Hover */}
      {hasTechnicalDetails && showDetails && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10">
          <span className="text-white text-xs font-bold">ℹ</span>
        </div>
      )}

      <div
        className="flex items-start gap-2 cursor-pointer"
        onClick={data.onClick}
        title="Click to view full details in cards view"
      >
        <div className={`${colors.iconBg} p-1.5 rounded-lg flex-shrink-0 ${colors.text}`}>
          {getIcon(data.type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h4 className={`font-bold text-sm ${colors.text} truncate`}>{data.label}</h4>
            {data.complexity && (
              <span className={`${colors.badge} text-white text-xs px-2 py-0.5 rounded font-bold uppercase`}>
                {data.complexity}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-600 mb-1">{data.category}</p>
          <p className="text-xs text-gray-500 line-clamp-2">{data.description}</p>
        </div>
      </div>

      {/* Technical Details (shown when hovering over entire card) */}
      {hasTechnicalDetails && showDetails && (
        <div
          className="mt-3 pt-3 border-t border-gray-200 space-y-2"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {data.authMethod && (
            <div className="flex items-start gap-1.5">
              <Lock className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-gray-700">Auth</div>
                <div className="text-[10px] text-gray-600 truncate">{data.authMethod}</div>
              </div>
            </div>
          )}
          {data.rateLimit && (
            <div className="flex items-start gap-1.5">
              <Zap className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-gray-700">Rate Limit</div>
                <div className="text-[10px] text-gray-600 truncate">{data.rateLimit}</div>
              </div>
            </div>
          )}
          {data.errorHandling && (
            <div className="flex items-start gap-1.5">
              <Shield className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-gray-700">Error Handling</div>
                <div className="text-[10px] text-gray-600 truncate">{data.errorHandling}</div>
              </div>
            </div>
          )}
          {data.constraints && data.constraints.length > 0 && (
            <div
              className="relative flex items-start gap-1.5"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <div className="w-3 h-3 flex items-center justify-center mt-0.5 flex-shrink-0">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-gray-700">Constraints</div>
                <div className="text-[10px] text-gray-600 truncate cursor-help">
                  {data.constraints.length} constraint{data.constraints.length !== 1 ? 's' : ''} (hover)
                </div>
              </div>

              {/* Tooltip - Smart positioning */}
              {showTooltip && (
                <div
                  className="fixed bg-gray-900 text-white rounded-lg p-3 shadow-2xl min-w-[250px] max-w-[350px] pointer-events-none"
                  style={{
                    zIndex: 9999,
                    left: '50%',
                    bottom: '100%',
                    transform: 'translateX(-50%)',
                    marginBottom: '8px'
                  }}
                >
                  <div className="text-xs font-bold mb-2 text-amber-300">Technical Constraints</div>
                  <ul className="space-y-1.5">
                    {data.constraints.map((constraint, idx) => (
                      <li key={idx} className="text-[11px] flex items-start gap-1.5">
                        <span className="text-amber-400 mt-0.5">•</span>
                        <span className="flex-1">{constraint}</span>
                      </li>
                    ))}
                  </ul>
                  {/* Arrow pointing down */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  systemNode: SystemNode
};

const VisualArchitectureDiagram: React.FC<VisualArchitectureDiagramProps> = ({
  architecture,
  onSystemClick
}) => {
  // Auto-layout algorithm using Dagre-like positioning
  const calculateNodePositions = useCallback(() => {
    const systems = architecture.systems || [];
    const connections = architecture.connections || [];

    // Group systems by type for better layout
    const sourceNodes: any[] = [];
    const middlewareNodes: any[] = [];
    const targetNodes: any[] = [];
    const otherNodes: any[] = [];

    systems.forEach(system => {
      if (system.type === 'source' || system.type === 'ui') {
        sourceNodes.push(system);
      } else if (system.type === 'middleware' || system.type === 'api') {
        middlewareNodes.push(system);
      } else if (system.type === 'target' || system.type === 'database') {
        targetNodes.push(system);
      } else {
        otherNodes.push(system);
      }
    });

    // Calculate positions in a left-to-right flow with better spacing
    const positions: { [key: string]: { x: number; y: number } } = {};
    const verticalSpacing = 280; // Increased spacing to prevent vertical overlap
    const horizontalSpacing = 550; // Increased spacing to prevent horizontal overlap and clarify arrows

    // Position source nodes on the left
    sourceNodes.forEach((node, idx) => {
      positions[node.id] = {
        x: 100,
        y: 100 + idx * verticalSpacing
      };
    });

    // Position middleware nodes in the middle
    const middlewareStartY = 100 + Math.max(0, (sourceNodes.length - middlewareNodes.length) * verticalSpacing / 2);
    middlewareNodes.forEach((node, idx) => {
      positions[node.id] = {
        x: 100 + horizontalSpacing,
        y: middlewareStartY + idx * verticalSpacing
      };
    });

    // Position target nodes on the right
    const targetStartY = 100 + Math.max(0, (sourceNodes.length - targetNodes.length) * verticalSpacing / 2);
    targetNodes.forEach((node, idx) => {
      positions[node.id] = {
        x: 100 + horizontalSpacing * 2,
        y: targetStartY + idx * verticalSpacing
      };
    });

    // Position other nodes (spread them across columns)
    otherNodes.forEach((node, idx) => {
      const column = idx % 2; // Alternate between columns
      positions[node.id] = {
        x: 100 + horizontalSpacing * (column === 0 ? 0.5 : 1.5),
        y: 100 + (sourceNodes.length + Math.floor(idx / 2)) * verticalSpacing
      };
    });

    return positions;
  }, [architecture]);

  // Convert architecture data to React Flow nodes
  const initialNodes: Node[] = useMemo(() => {
    const positions = calculateNodePositions();
    const integrationPoints = architecture.integrationPoints || [];

    return (architecture.systems || []).map((system) => {
      const integration = integrationPoints.find(ip => ip.system === system.id);
      const position = positions[system.id] || system.position || { x: 0, y: 0 };

      return {
        id: system.id,
        type: 'systemNode',
        position,
        draggable: true, // Enable dragging
        data: {
          label: system.name,
          type: system.type,
          category: system.category,
          description: system.description,
          complexity: integration?.complexity,
          hasAuth: !!integration?.authMethod,
          authMethod: integration?.authMethod,
          rateLimit: integration?.rateLimit,
          errorHandling: integration?.errorHandling,
          constraints: integration?.constraints,
          onClick: () => onSystemClick?.(system.id)
        }
      };
    });
  }, [architecture, calculateNodePositions, onSystemClick]);

  // Convert connections to React Flow edges
  const initialEdges: Edge[] = useMemo(() => {
    return (architecture.connections || []).map((conn) => {
      // Determine edge color based on data type
      let edgeColor = '#6366f1'; // default indigo
      let strokeWidth = 2;
      let animated = false;

      if (conn.dataType?.toLowerCase().includes('sensitive') ||
          conn.dataType?.toLowerCase().includes('credential')) {
        edgeColor = '#dc2626'; // red for sensitive data
        strokeWidth = 3;
      } else if (conn.dataType?.toLowerCase().includes('real-time') ||
                 conn.dataType?.toLowerCase().includes('event')) {
        edgeColor = '#16a34a'; // green for real-time
        animated = true;
      }

      return {
        id: conn.id,
        source: conn.from,
        target: conn.to,
        label: `${conn.protocol}\n${conn.dataType}`,
        animated,
        style: {
          stroke: edgeColor,
          strokeWidth
        },
        labelBgStyle: {
          fill: '#ffffff',
          fillOpacity: 0.9
        },
        labelStyle: {
          fontSize: 11,
          fontWeight: 600,
          fill: '#374151'
        },
        type: conn.direction === 'bidirectional' ? 'default' : 'smoothstep',
        markerEnd: {
          type: 'arrowclosed',
          color: edgeColor
        },
        ...(conn.direction === 'bidirectional' && {
          markerStart: {
            type: 'arrowclosed',
            color: edgeColor
          }
        })
      };
    });
  }, [architecture]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  return (
    <div className="h-[600px] w-full bg-gray-50 rounded-lg border-2 border-gray-200">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls />
        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="bg-white border border-gray-300 rounded"
        />
      </ReactFlow>

      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white border-2 border-gray-300 rounded-lg p-3 shadow-lg">
        <h5 className="text-xs font-bold text-gray-900 mb-2">Legend</h5>
        <div className="space-y-2">
          <div>
            <div className="text-[10px] font-semibold text-gray-600 mb-1">Complexity</div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-600 rounded"></div>
                <span className="text-xs text-gray-700">Simple</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                <span className="text-xs text-gray-700">Moderate</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-600 rounded"></div>
                <span className="text-xs text-gray-700">Complex</span>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-[8px] font-bold">ℹ</span>
              </div>
              <span className="text-xs text-gray-700">Integration Details</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualArchitectureDiagram;
